<i18n>
{
  "en": {
    "fontModalTitle": "Edit font",
    "fontFamily": "Font family",
    "confirm": "Confirm",
    "cancel": "Cancel",
    "confirmTitle": "Are you sure you want to create the font?",
    "confirmText": "This cannot be undone or modified",
    "validationError": "Please, review all fields",
    "errorInFont": "Error in font",
    "emptyFieldError": "Required field",
    "saveFontSuccess": "Font created successfully",
    "saveFontError": "There was an error creating the product, try again later",
    "sameFileNameError": "Name already in use",
    "sameFileError": "\"{0}\" font has the same file name. Please choose other file.",
    "minMaxLenError": "Font name too large",
    "fileInput": "Add file"
  },
  "es": {
    "fontModalTitle": "Crear fuente",
    "fontFamily": "Familia de fuente",
    "confirm": "Confirmar",
    "cancel": "Cancelar",
    "confirmTitle": "¿Estás seguro de que deseas crear la fuente?",
    "confirmText": "Esto no se puede deshacer ni modificar",
    "validationError": "Por favor, revise todos los campos",
    "errorInFont": "Error con la fuente",
    "emptyFieldError": "Campo obligatorio",
    "saveFontSuccess": "Fuente creada correctamente",
    "saveFontError": "Hubo un error creando la fuente, inténtelo de nuevo más tarde.",
    "sameFileNameError": "Ese nombre ya está en uso",
    "sameFileError": "La fuente \"{0}\" tiene el mismo archivo. Elija otra.",
    "minMaxLenError": "Nombre de la fuente excesivamente grande",
    "fileInput": "Introduzca el archivo"
  }
}
</i18n>

<template>
  <v-dialog v-model="visible" @click:outside="onCancel" max-width="600px" scrollable persistent>
    <dynameat-confirmation-modal :visible="confirmModalVisible" :title="$t('confirmTitle')"
                                 :description="$t('confirmText')" :confirm-text="$t('confirm')"
                                 @confirm="onConfirmConfirmModal" @cancel="onCloseConfirmModal"/>
    <v-card v-if="visible" height="100%" class="px-7 py-5">
      <div class="d-flex align-center justify-space-between">
        <v-card-title class="word-break pa-0">{{ this.$t('fontModalTitle') }}</v-card-title>
      </div>
      <v-container class="pa-0">
        <v-form v-model="valid" class="d-flex flex-column align-left master-font-form form-inputs" ref="form">
          <v-text-field :label="$t('fontFamily')" v-model="fontFamily" :rules="fontNameRules"
                        class="required ma-0 pa-0"/>
          <v-file-input class="pa-0 ma-0 required" v-model="file" :rules="fileRules" :accept="validFiletypes"
                        :label="$t('fileInput')"/>
          <dynameat-modal-buttons @cancel="onCancel" @confirm="onConfirm" :confirmText="$t('confirm')" :confirmDisabled="!valid"/>
        </v-form>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>

import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import DynameatModalButtons from '@dynameat/components/DynameatModalButtons';
import DynameatModalHeader from '@dynameat/components/DynameatModalHeader';
import { createErrorNotification, createSuccessNotification } from '@ulibs/unnotificationsqueue';
import { maxSize, minMaxChars, notEmptyString } from '@core/helpers/formValidators';
import { Font } from '../models';
import { MAX_FONT_SIZE, VALID_FONT_EXTENSIONS } from '../models/font';

@Component
@Components({ DynameatConfirmationModal, DynameatModalHeader, DynameatModalButtons })
@MapGetters(['menuFontList'])
@MapActions(['addNotification', 'removeNotificationById', 'createMenuFontRequest'])
export default class FontCreateModal extends Vue {
  @Prop({ type: Boolean, default: false }) visible;

  valid = false;

  confirmModalVisible = false;

  file = null;

  fontFamily = '';

  onConfirm() {
    this.$refs.form?.validate();
    if (this.valid) {
      this.onOpenConfirmModal();
    } else {
      this.addNotification(createErrorNotification(this.$t('validationError'), { id: 'fontValidationError' }));
    }
  }

  onCancel() {
    this.$emit('cancel');
    this.clearOwnData();
  }

  clearOwnData() {
    this.fontFamily = '';
    this.file = null;
  }

  onOpenConfirmModal() {
    this.confirmModalVisible = true;
  }

  onCloseConfirmModal() {
    this.confirmModalVisible = false;
  }

  get validFiletypes() {
    return VALID_FONT_EXTENSIONS.map((el) => `.${el}`).join(',');
  }

  get fileRules() {
    return [(v) => !!v || this.$t('emptyFieldError'),
      this.fontFileInUseValidator,
      maxSize(this.$t('fileSizeError', MAX_FONT_SIZE.toString()), MAX_FONT_SIZE)];
  }

  get fontNameRules() {
    return [notEmptyString(this.$t('emptyFieldError')), minMaxChars(this.$t('minMaxLenError'), 1, 64), this.sameNameValidator];
  }

  async onConfirmConfirmModal() {
    this.confirmModalVisible = false;
    const fontObject = new Font(undefined, this.fontFamily, false, '', this.file);
    const font = await this.createFont(fontObject.getFormData());
    this.$emit('confirm', font);
    this.clearOwnData();
  }

  async createFont(font) {
    try {
      const fontResponse = await this.createMenuFontRequest(font);
      this.addNotification(createSuccessNotification(this.$t('saveFontSuccess'), {}));
      return fontResponse;
    } catch (e) {
      this.addNotification(createErrorNotification(this.$t('saveFontError')));
      return false;
    }
  }

  sameNameValidator(fontName) {
    return this.menuFontList.some((font) => font.fontFamily === fontName) ? this.$t('sameFileNameError') : true;
  }

  fontFileInUseValidator(file) {
    let foundFont = null;
    if (file) {
      foundFont = this.menuFontList.find((font) => {
        const escapedFileName = `/${escape(file.name)}`;
        return font.url.includes(escapedFileName);
      });
    }
    return foundFont ? this.$t('sameFileError', [foundFont.fontFamily]) : true;
  }
}
</script>
<style scoped lang="scss">

.form-inputs {
  > div {
    margin-bottom: 15px;
  }
}

.progress-enter-active, .progress-leave-active {
  transition: all 0.3s ease;
}

.progress-enter, .progress-leave-active {
  opacity: 0;
}

.word-break {
  word-break: break-word;
}

.same-gray {
  color: rgba(0, 0, 0, 0.6)
}

.pointer {
  cursor: pointer;
}
</style>

<style>
.master-font-form .v-text-field__details {
  padding-top: 5px;
}

.master-font-form .v-messages__message {
  line-height: 14px;
  font-size: 13px
}

.master-font-form .master-font-input .v-messages {
  min-height: 0;
}

.required label::after {
  content: " *";
  color: red;
}
</style>
